import { purple } from '@synoptic/ui-kit/palette.js';
import { responsiveStyle } from '@synoptic/ui-kit/responsive/responsive-style.ts';
import { themeVars } from '@synoptic/ui-kit/theme/vars.ts';
import { borderRadius, space } from '@synoptic/ui-kit/tokens.ts';
import { typography } from '@synoptic/ui-kit/typography.ts';
import { ellipsis } from '@synoptic/ui-kit/utility.css.ts';
import { style } from '@vanilla-extract/css';

const gradientMainColorLight = `color-mix(in srgb, ${purple[300]}, transparent 30%)`;
const gradientMainColorDark = `color-mix(in srgb, ${purple[700]}, transparent 50%)`;
const gradientBgColorLight = `color-mix(in srgb, ${purple[50]}, transparent 30%)`;
const gradientBgColorDark = `color-mix(in srgb, ${themeVars.container.primary.background.idle}, transparent 50%)`;

export const streamBannerCard = style([
  {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  responsiveStyle({
    mobile: {
      padding: space.s16,
      background: `radial-gradient(31.87% 243.97% at 60.74% 62.33%, light-dark(${gradientMainColorLight}, ${gradientMainColorDark}) 0%, light-dark(${gradientBgColorLight}, ${gradientBgColorDark}) 120%)`,
      gap: space.s56,
    },
    tablet: {
      padding: space.s32,
      background: `radial-gradient(31.87% 243.97% at 60.74% 62.33%, light-dark(${gradientMainColorLight}, ${gradientMainColorDark}) 0%, light-dark(${gradientBgColorLight}, ${gradientBgColorDark}) 100%)`,
      gap: 100,
    },
  }),
]);

export const paidStreamBannerCard = style([
  {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: themeVars.container.tertiary.background.idle,
    backgroundImage: 'url(./media/stream-bg-gradient.svg)',
    backgroundSize: 'cover',
    backgroundPositionX: 'center',
    backgroundPositionY: 'center',
    boxShadow: 'none',
    outline: `1px solid ${themeVars.color.border.tertiary}`,
    borderRadius: borderRadius.r12,
  },
  responsiveStyle({
    mobile: {
      gap: space.s70,
      padding: space.s16,
    },
    tablet: {
      gap: 140,
      padding: space.s32,
    },
  }),
]);

export const streamBannerContent = style([
  {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    maxWidth: 630,
  },
]);

export const streamDescription = style([
  typography.bodyMd,
  {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: themeVars.container.foreground.secondary,
  },
]);

export const labelContainer = style([
  {
    overflowX: 'auto',
    scrollbarWidth: 'none',
  },
]);

export const paidStreamLabelContainer = style([
  {
    overflow: 'hidden',
  },
  responsiveStyle({
    mobile: {
      flex: '0 1 auto',
      maxWidth: 'none',
    },
    tablet: {},
    desktop: {
      /**
       * hack to make block shrink fully before the next block
       * https://stackoverflow.com/questions/67858284/how-to-have-one-item-shrink-fully-before-another-starts-to-shrink
       */
      flex: '1 0 0',
      maxWidth: 'fit-content',
    },
  }),
]);

export const paidStreamLabel = style([
  {
    selectors: {
      '&:is(&)': {
        width: 'auto',
        maxWidth: '100%',
      },
    },
  },
]);

export const freeStreamBannerFooter = style([
  {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: space.s16,
  },
  responsiveStyle({
    mobile: {
      flexWrap: 'wrap',
    },
    tablet: {},
    desktop: {
      flexWrap: 'nowrap',
    },
  }),
]);

export const paidStreamBannerFooter = style([
  freeStreamBannerFooter,
  {
    paddingTop: space.s2,
    justifyContent: 'start',
    alignItems: 'center',
  },
  responsiveStyle({
    mobile: {
      borderTop: 'none',
    },
    tablet: {},
    desktop: {
      gap: 0,
      borderTop: `1px solid ${themeVars.color.border.tertiary}`,
    },
  }),
]);

export const paidBannerFooterSection = style([
  responsiveStyle({
    mobile: {
      padding: 0,
      borderRight: `none`,
    },
    tablet: {},
    desktop: {
      padding: `${space.s8}px ${space.s14}px`,
      borderRight: `1px solid ${themeVars.color.border.tertiary}`,

      ':last-child': {
        borderRight: 'none',
        padding: `${space.s8}px 0 ${space.s8}px ${space.s20}px`,
      },

      ':first-child': {
        padding: `${space.s8}px ${space.s20}px ${space.s8}px 0`,
      },
    },
  }),
]);

export const freeBannerActionsBlock = style([
  {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
  responsiveStyle({
    mobile: {
      width: '100%',
      gap: space.s8,
      flexWrap: 'wrap',
    },
    tablet: {
      width: 'auto',
      justifyContent: 'end',
    },
    desktop: {
      flexWrap: 'nowrap',
      gap: space.s16,
    },
  }),
]);

export const paidBannerActionsBlock = style([
  freeBannerActionsBlock,
  responsiveStyle({
    mobile: {
      flexDirection: 'column',
      alignItems: 'start',
      borderTop: `1px solid ${themeVars.color.border.tertiary}`,
      paddingTop: space.s12,
      flex: '1 1 auto',
      marginLeft: 0,
    },
    tablet: {
      flexDirection: 'row',
      alignItems: 'center',

      selectors: {
        '&:is(&)': {
          width: '100%',
        },
      },
    },
    desktop: {
      borderTop: 0,
      paddingTop: 0,
      flex: '0 1 auto',
      marginLeft: 'auto',

      selectors: {
        '&:is(&)': {
          width: 'auto',
        },
      },
    },
  }),
]);

export const subscriptionInfo = style([
  {
    marginRight: 'auto',
  },
]);

export const followStreamButton = style([
  responsiveStyle({
    mobile: {
      width: '100%',
    },
    tablet: {
      width: 'fit-content',
    },
  }),
]);

export const buttonsBlock = style([
  {
    display: 'flex',
    gap: space.s8,
  },
  responsiveStyle({
    mobile: {
      width: '100%',
      flexDirection: 'column',
    },
    tablet: {
      width: 'auto',
      flexDirection: 'row',
      alignItems: 'center',
    },
  }),
]);

export const fallBackContainer = style([
  streamBannerCard,
  {
    padding: space.s32,
    width: '100%',
  },
  responsiveStyle({
    mobile: {
      background: themeVars.container.secondary.background.idle,
      height: 300,
    },
    tablet: {
      background: themeVars.container.secondary.background.idle,
      height: 350,
    },
  }),
]);

export const includedInPackageLabel = style([
  ellipsis,
  {
    whiteSpace: 'nowrap',
  },
]);

export const labelsContainer = style([
  {
    display: 'flex',
    flexDirection: 'column',
    gap: space.s1,
  },
  responsiveStyle({
    mobile: {
      marginRight: 0,
      alignItems: 'center',
      width: '100%',
    },
    tablet: {
      marginRight: space.s8,
      alignItems: 'start',
      width: 'fit-content',
    },
  }),
]);
